(function () {
  const elementName = 'homepage-slideshow';

  if (customElements.get(elementName)) {
    return;
  }

  class HomepageSlideshow extends HTMLElement {
    constructor() {
      super();

      this.track = null;
      this.slides = [];
      this.pagination = null;
      this.paginationButtons = [];
      this.activeIndex = 0;
      this.perPage = 1;
      this.pageCount = 1;
      this.autoplayTimer = null;
      this.raf = null;
      this.resizeObserver = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-homepage-slideshow-track]');
      this.slides = Array.from(this.querySelectorAll('[data-homepage-slideshow-slide]'));
      this.pagination = this.querySelector('[data-homepage-slideshow-pagination]');

      if (!this.track || this.slides.length === 0) {
        return;
      }

      this.setupEvents();
      this.rebuildPagination();
      this.updateActiveFromScroll();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.destroy();
    }

    setupEvents() {
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);

      if (this.shouldPauseOnHover()) {
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }
    }

    destroy() {
      this.stopAutoplay();

      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKeydown);
      }

      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      } else {
        window.removeEventListener('resize', this.onResize);
      }

      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    shouldAutoplay() {
      return this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion;
    }

    shouldPauseOnHover() {
      return this.dataset.pauseOnHover === 'true';
    }

    shouldShowDots() {
      return this.dataset.showDots === 'true' && this.pagination && this.slides.length > 1;
    }

    getInterval() {
      const interval = Number(this.dataset.autoplayInterval || 5000);
      return Number.isFinite(interval) && interval >= 1000 ? interval : 5000;
    }

    calculatePerPage() {
      if (!this.track || this.slides.length === 0) {
        return 1;
      }

      const firstSlide = this.slides[0];
      const slideWidth = firstSlide.getBoundingClientRect().width;
      const trackWidth = this.track.clientWidth;

      if (!slideWidth || !trackWidth) {
        return 1;
      }

      return Math.max(1, Math.min(this.slides.length, Math.round(trackWidth / slideWidth)));
    }

    rebuildPagination() {
      this.perPage = this.calculatePerPage();
      this.pageCount = Math.max(1, Math.ceil(this.slides.length / this.perPage));

      if (!this.shouldShowDots()) {
        if (this.pagination) {
          this.pagination.innerHTML = '';
        }
        this.paginationButtons = [];
        return;
      }

      this.pagination.innerHTML = '';
      this.paginationButtons = [];

      for (let pageIndex = 0; pageIndex < this.pageCount; pageIndex += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'homepage-slideshow__pagination-button';
        button.setAttribute('aria-label', `Go to slideshow page ${pageIndex + 1}`);
        button.addEventListener('click', () => {
          this.goToPage(pageIndex);
          this.restartAutoplay();
        });

        this.pagination.appendChild(button);
        this.paginationButtons.push(button);
      }

      this.updatePagination();
    }

    goToPage(pageIndex) {
      const clampedPage = Math.max(0, Math.min(this.pageCount - 1, pageIndex));
      const targetIndex = Math.min(this.slides.length - 1, clampedPage * this.perPage);
      this.goToSlide(targetIndex);
    }

    goToSlide(index) {
      const target = this.slides[index];

      if (!target || !this.track) {
        return;
      }

      this.track.scrollTo({
        left: target.offsetLeft - this.track.offsetLeft,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }

    updateActiveFromScroll() {
      if (!this.track || this.slides.length === 0) {
        return;
      }

      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const distance = Math.abs(trackCenter - slideCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.activeIndex = closestIndex;

      this.slides.forEach((slide, index) => {
        const isActive = index === this.activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.updatePagination();
    }

    updatePagination() {
      if (!this.paginationButtons.length) {
        return;
      }

      const activePage = Math.min(this.pageCount - 1, Math.floor(this.activeIndex / this.perPage));

      this.paginationButtons.forEach((button, index) => {
        const isActive = index === activePage;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    onScroll() {
      if (this.raf) {
        return;
      }

      this.raf = window.requestAnimationFrame(() => {
        this.updateActiveFromScroll();
        this.raf = null;
      });
    }

    onResize() {
      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
      }

      this.raf = window.requestAnimationFrame(() => {
        const previousPerPage = this.perPage;
        this.rebuildPagination();

        if (previousPerPage !== this.perPage) {
          this.goToSlide(this.activeIndex);
        }

        this.updateActiveFromScroll();
        this.raf = null;
      });
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextPage = Math.floor(this.activeIndex / this.perPage) + direction;

      if (nextPage < 0) {
        this.goToPage(this.pageCount - 1);
      } else if (nextPage >= this.pageCount) {
        this.goToPage(0);
      } else {
        this.goToPage(nextPage);
      }

      this.restartAutoplay();
    }

    startAutoplay() {
      if (!this.shouldAutoplay()) {
        return;
      }

      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(() => {
        const activePage = Math.floor(this.activeIndex / this.perPage);
        const nextPage = activePage + 1 >= this.pageCount ? 0 : activePage + 1;
        this.goToPage(nextPage);
      }, this.getInterval());
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    onMouseEnter() {
      this.stopAutoplay();
    }

    onMouseLeave() {
      this.startAutoplay();
    }

    onFocusIn() {
      this.stopAutoplay();
    }

    onFocusOut() {
      this.startAutoplay();
    }
  }

  customElements.define(elementName, HomepageSlideshow);

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-homepage-slideshow-slide]');
    const carousel = event.target.closest(elementName);

    if (!slide || !carousel || typeof carousel.goToSlide !== 'function') {
      return;
    }

    carousel.stopAutoplay();
    carousel.goToSlide(Number(slide.dataset.slideIndex || 0));
  });
})();
